import { TenantApiClient } from '../api/TenantApiClient';
import type { TenantMetadata } from '../types';
import { assertTenantMetadata } from '../validators/assertTenantMetadata';

const CACHE_KEY_PREFIX = 'tenant_metadata:v3:';

export const getTenantMetadataCacheKey = (partnerIdn: string): string =>
	`${CACHE_KEY_PREFIX}${partnerIdn}`;

export interface TenantMetadataStorage {
	get<T>(key: string): T | null;
	set<T>(key: string, value: T): void;
	remove(key: string): void;
}

export interface TenantMetadataInitOptions {
	cmsApiUrl?: string;
	partnerIdn: string;
	storage: TenantMetadataStorage;
}

export interface TenantMetadataStateSnapshot {
	activePartnerIdn: string | null;
	backgroundRefreshPromise: Promise<void> | null;
	initialLoadPromise: Promise<void> | null;
	metadata: TenantMetadata | null;
}

export class TenantMetadataService {
	private _metadata: TenantMetadata | null = null;
	private activePartnerIdn: string | null = null;
	private activeCmsApiUrl: string | undefined;
	private initialLoadPromise: Promise<void> | null = null;
	private backgroundRefreshPromise: Promise<void> | null = null;

	public get metadata(): TenantMetadata {
		if (!this._metadata) {
			throw new Error(
				'Tenant metadata not initialized. Call tenantMetadata.init() first.'
			);
		}

		return this._metadata;
	}

	public async init(options: TenantMetadataInitOptions): Promise<void> {
		const partnerIdn = options.partnerIdn.trim();

		if (!partnerIdn) {
			throw new Error(
				'Tenant metadata initialization requires a non-empty partnerIdn.'
			);
		}

		this.activeCmsApiUrl = options.cmsApiUrl;

		const cacheKey = getTenantMetadataCacheKey(partnerIdn);

		if (this.activePartnerIdn === partnerIdn && this._metadata) {
			this.refreshInBackground(partnerIdn, options.storage, cacheKey);
			return;
		}

		if (this.initialLoadPromise && this.activePartnerIdn === partnerIdn) {
			return this.initialLoadPromise;
		}

		this.activePartnerIdn = partnerIdn;

		const cachedMetadata = this.readCachedMetadata(
			partnerIdn,
			options.storage,
			cacheKey
		);

		if (cachedMetadata) {
			this._metadata = this.normalizeMetadata(cachedMetadata);
			this.refreshInBackground(partnerIdn, options.storage, cacheKey);
			return;
		}

		this._metadata = null;

		const loadPromise = this.fetchAndPersist(
			partnerIdn,
			options.storage,
			cacheKey
		).finally(() => {
			if (this.initialLoadPromise === loadPromise) {
				this.initialLoadPromise = null;
			}
		});

		this.initialLoadPromise = loadPromise;

		await loadPromise;
	}

	public captureState(): TenantMetadataStateSnapshot {
		return {
			activePartnerIdn: this.activePartnerIdn,
			backgroundRefreshPromise: this.backgroundRefreshPromise,
			initialLoadPromise: this.initialLoadPromise,
			metadata: this._metadata
		};
	}

	public restoreState(state: TenantMetadataStateSnapshot): void {
		this.activePartnerIdn = state.activePartnerIdn;
		this.backgroundRefreshPromise = state.backgroundRefreshPromise;
		this.initialLoadPromise = state.initialLoadPromise;
		this._metadata = state.metadata
			? this.normalizeMetadata(state.metadata)
			: null;
	}

	private normalizeMetadata(metadata: TenantMetadata): TenantMetadata {
		if (
			metadata.portal_settings.is_stripe_connect_enabled !== undefined ||
			metadata.portal_settings.stripe_connect_enabled === undefined
		) {
			return metadata;
		}
		return {
			...metadata,
			portal_settings: {
				...metadata.portal_settings,
				is_stripe_connect_enabled:
					metadata.portal_settings.stripe_connect_enabled
			}
		};
	}

	private readCachedMetadata(
		partnerIdn: string,
		storage: TenantMetadataStorage,
		cacheKey: string
	): TenantMetadata | null {
		try {
			const cached = storage.get<unknown>(cacheKey);

			if (cached === null || cached === undefined) {
				return null;
			}

			assertTenantMetadata(cached, partnerIdn, 'cached metadata');

			return cached;
		} catch {
			try {
				storage.remove(cacheKey);
			} catch {
				return null;
			}

			return null;
		}
	}

	private refreshInBackground(
		partnerIdn: string,
		storage: TenantMetadataStorage,
		cacheKey: string
	): void {
		if (this.backgroundRefreshPromise && this.activePartnerIdn === partnerIdn) {
			return;
		}

		const refreshPromise = this.fetchAndPersist(partnerIdn, storage, cacheKey)
			.catch(() => undefined)
			.finally(() => {
				if (this.backgroundRefreshPromise === refreshPromise) {
					this.backgroundRefreshPromise = null;
				}
			});

		this.backgroundRefreshPromise = refreshPromise;
	}

	private async fetchAndPersist(
		partnerIdn: string,
		storage: TenantMetadataStorage,
		cacheKey: string
	): Promise<void> {
		const metadata = await TenantApiClient.getTenantMetadata(
			partnerIdn,
			this.activeCmsApiUrl
		);

		if (this.activePartnerIdn !== partnerIdn) {
			return;
		}

		this._metadata = this.normalizeMetadata(metadata);

		try {
			storage.set(cacheKey, metadata);
		} catch {
			return;
		}
	}
}

export const tenantMetadata = new TenantMetadataService();
